import { Checkbox, FormControlLabel, Stack } from "@mui/material";
import { ControlledModificationReasonField } from "@src/appV2/ExperimentalHomeHealth/components/ModificationReasonField";
import { Text } from "@src/appV2/redesign/components/Text";
import { ValidatedTextArea } from "@src/appV2/redesign/components/ValidatedTextArea";

import {
  CASE_DESCRIPTION_FIELD_NAME,
  CASE_DESCRIPTION_MAX_LENGTH,
  CASE_DESCRIPTION_MIN_LENGTH,
} from "../../caseDescriptionValidation";

const SPECIALTIES = [
  "Wound Care",
  "Wound Vac",
  "PICC Line",
  "Dementia",
  "Trache",
  "IV",
  "TPN",
] as const;

export interface CaseDetailsData {
  specialties: string[];
  description: string;
  modificationReason: string;
}

interface Props {
  data: CaseDetailsData;
  onChange: (field: keyof CaseDetailsData, value: string | string[]) => void;
}

/** Case-level details (specialties, description, and the admin-only modification reason). */
export function MobileCaseDetailsFields({ data, onChange }: Props) {
  function toggleSpecialty(specialty: string) {
    const updated = data.specialties.includes(specialty)
      ? data.specialties.filter((s) => s !== specialty)
      : [...data.specialties, specialty];
    onChange("specialties", updated);
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Text semibold variant="body1">
          Specialties
        </Text>
        <Stack>
          {SPECIALTIES.map((specialty) => (
            <FormControlLabel
              key={specialty}
              label={specialty}
              control={
                <Checkbox
                  checked={data.specialties.includes(specialty)}
                  onChange={() => {
                    toggleSpecialty(specialty);
                  }}
                />
              }
            />
          ))}
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <Text semibold variant="body1">
          Description
        </Text>
        <Text variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
          Visible to professionals
        </Text>
        <ValidatedTextArea
          value={data.description}
          fieldName={CASE_DESCRIPTION_FIELD_NAME}
          minCharacters={CASE_DESCRIPTION_MIN_LENGTH}
          maxCharacters={CASE_DESCRIPTION_MAX_LENGTH}
          minRows={4}
          fullWidth
          inputProps={{ "aria-label": CASE_DESCRIPTION_FIELD_NAME, "data-dd-privacy": "mask" }}
          sx={{
            mt: 0.5,
            bgcolor: "background.paper",
            borderRadius: 2,
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
          onChange={(value) => {
            onChange("description", value);
          }}
        />
      </Stack>

      <ControlledModificationReasonField
        value={data.modificationReason}
        onChange={(value) => {
          onChange("modificationReason", value);
        }}
      />
    </Stack>
  );
}
